import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Calendar, User, Loader2, Play, MessageCircle, Send, Trash2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple inline link converter with markdown support
// Supports: [link text](url) and raw URLs
const LinkedText = ({ text, className = "" }) => {
  if (!text) return null;
  
  // Combined regex: markdown links [text](url) OR raw URLs
  const combinedRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s<\[\]]+[^<.,:;"')\]\s])/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = combinedRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    if (match[1] && match[2]) {
      // Markdown link: [text](url)
      parts.push(
        <a
          key={match.index}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-purple-light hover:text-white transition-colors underline underline-offset-4"
        >
          {match[1]}
        </a>
      );
    } else if (match[3]) {
      // Raw URL
      const url = match[3];
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-purple-light hover:text-white transition-colors underline underline-offset-4"
        >
          {url.length > 50 ? url.slice(0, 50) + '...' : url}
        </a>
      );
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  if (parts.length === 0) {
    return <span className={className}>{text}</span>;
  }
  
  return <span className={className}>{parts}</span>;
};

// Parse content and convert URLs to links, embed videos
const RichContent = ({ content }) => {
  if (!content) return null;
  
  // Regex patterns
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S*)?/g;
  const vimeoRegex = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)(?:\S*)?/g;
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  
  // Split content into paragraphs
  const paragraphs = content.split('\n\n');
  
  const renderParagraph = (paragraph, pIndex) => {
    // Check for YouTube video on its own line
    const youtubeMatch = paragraph.trim().match(/^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S*)?$/);
    if (youtubeMatch) {
      return (
        <div key={pIndex} className="my-8 aspect-video rounded-lg overflow-hidden bg-void-surface">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeMatch[1]}`}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      );
    }
    
    // Check for Vimeo video on its own line
    const vimeoMatch = paragraph.trim().match(/^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)(?:\S*)?$/);
    if (vimeoMatch) {
      return (
        <div key={pIndex} className="my-8 aspect-video rounded-lg overflow-hidden bg-void-surface">
          <iframe
            src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
            title="Vimeo video"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      );
    }
    
    // Process text with inline links (markdown and raw URLs)
    // Combined regex: markdown links [text](url) OR raw URLs
    const combinedRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s<\[\]]+[^<.,:;"')\]\s])/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = combinedRegex.exec(paragraph)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(paragraph.slice(lastIndex, match.index));
      }
      
      if (match[1] && match[2]) {
        // Markdown link: [text](url)
        const url = match[2];
        const linkText = match[1];
        
        // Check if it's a video link
        const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        const vimMatch = url.match(/vimeo\.com\/(\d+)/);
        
        if (ytMatch || vimMatch) {
          parts.push(
            <a
              key={match.index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-purple-light hover:text-white transition-colors underline underline-offset-4"
            >
              <Play className="w-3 h-3" />
              {linkText}
            </a>
          );
        } else {
          parts.push(
            <a
              key={match.index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-light hover:text-white transition-colors underline underline-offset-4"
            >
              {linkText}
            </a>
          );
        }
      } else if (match[3]) {
        // Raw URL
        const url = match[3];
        
        // Check if it's a YouTube link (inline)
        const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        const vimMatch = url.match(/vimeo\.com\/(\d+)/);
        
        if (ytMatch || vimMatch) {
          parts.push(
            <a
              key={match.index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-purple-light hover:text-white transition-colors underline underline-offset-4"
            >
              <Play className="w-3 h-3" />
              {ytMatch ? 'Watch on YouTube' : 'Watch on Vimeo'}
            </a>
          );
        } else {
          parts.push(
            <a
              key={match.index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-light hover:text-white transition-colors underline underline-offset-4"
            >
              {url.length > 50 ? url.slice(0, 50) + '...' : url}
            </a>
          );
        }
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < paragraph.length) {
      parts.push(paragraph.slice(lastIndex));
    }
    
    // If no links found, return plain paragraph
    if (parts.length === 0) {
      return (
        <p key={pIndex} className="mb-6">
          {paragraph}
        </p>
      );
    }
    
    return (
      <p key={pIndex} className="mb-6">
        {parts}
      </p>
    );
  };
  
  return (
    <div className="text-slate-300 leading-relaxed">
      {paragraphs.map((para, index) => renderParagraph(para, index))}
    </div>
  );
};

export default function NewsDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchArticle();
    fetchComments();
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/news/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error("Error fetching article:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    setCommentsLoading(true);
    try {
      const response = await axios.get(`${API}/news/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      const response = await axios.post(
        `${API}/news/${id}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      setComments([response.data, ...comments]);
      setNewComment("");
      toast.success("Comment posted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    
    try {
      await axios.delete(`${API}/news/${id}/comments/${commentId}`, {
        withCredentials: true
      });
      setComments(comments.filter(c => c.comment_id !== commentId));
      toast.success("Comment deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete comment");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-400 mb-4">Article not found</p>
        <Link to="/news" className="text-purple-light hover:text-white">
          Back to News
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>{article.title} | AI Film Society</title>
        <meta name="description" content={article.excerpt || article.content?.slice(0, 160)} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt || article.content?.slice(0, 160)} />
        <meta property="og:image" content={article.image_url || "https://aifilmsociety.org/images/logo-circle-white.png"} />
        <meta property="og:url" content={`https://aifilmsociety.org/news/${id}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt || article.content?.slice(0, 160)} />
        <meta name="twitter:image" content={article.image_url || "https://aifilmsociety.org/images/logo-circle-white.png"} />
        <link rel="canonical" href={`https://aifilmsociety.org/news/${id}`} />
      </Helmet>
      
      <article className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link 
            to="/news" 
            data-testid="back-to-news"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to News
          </Link>

          <div className="mb-8">
            <span className="text-xs uppercase tracking-wider text-purple-light font-mono">
              {article.category}
            </span>
            <h1 className="font-syne text-3xl md:text-5xl font-bold text-white mt-4 mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {article.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(article.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
          </div>

          {/* Featured Video (if provided) */}
          {article.video_url && (
            <div className="aspect-video mb-12 rounded-lg overflow-hidden bg-void-surface">
              {article.video_url.includes('youtube.com') || article.video_url.includes('youtu.be') ? (
                <iframe
                  src={`https://www.youtube.com/embed/${article.video_url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)?.[1] || ''}`}
                  title="Featured video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : article.video_url.includes('vimeo.com') ? (
                <iframe
                  src={`https://player.vimeo.com/video/${article.video_url.match(/vimeo\.com\/(\d+)/)?.[1] || ''}`}
                  title="Featured video"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : (
                <video src={article.video_url} controls className="w-full h-full" />
              )}
            </div>
          )}

          {/* Featured Image (if no video) */}
          {article.image_url && !article.video_url && (
            <div className="aspect-video mb-12 overflow-hidden">
              <img 
                src={article.image_url}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="prose prose-invert prose-lg max-w-none">
            <p className="text-xl text-slate-300 mb-8 font-light leading-relaxed">
              <LinkedText text={article.excerpt} />
            </p>
            <RichContent content={article.content} />
          </div>

          {/* Comments Section */}
          <div className="mt-16 pt-12 border-t border-white/10">
            <h3 className="font-syne text-2xl font-bold text-white mb-8 flex items-center gap-3">
              <MessageCircle className="w-6 h-6 text-purple" />
              Comments
              {comments.length > 0 && (
                <span className="text-sm font-normal text-slate-400">({comments.length})</span>
              )}
            </h3>

            {/* Comment Form */}
            {user ? (
              <form onSubmit={handleSubmitComment} className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple/20 flex items-center justify-center">
                    <span className="text-purple-light font-medium">
                      {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-grow">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Share your thoughts..."
                      rows={3}
                      className="w-full px-4 py-3 bg-void-surface border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-purple/50 resize-none"
                      data-testid="comment-input"
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        type="submit"
                        disabled={submittingComment || !newComment.trim()}
                        className="px-4 py-2 bg-purple hover:bg-purple-light text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        data-testid="submit-comment-btn"
                      >
                        {submittingComment ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Post Comment
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <div className="mb-8 p-6 bg-void-surface border border-white/10 rounded-lg text-center">
                <p className="text-slate-400 mb-4">Sign in to join the conversation</p>
                <Link
                  to="/login"
                  className="inline-block px-6 py-2 bg-purple hover:bg-purple-light text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Sign In
                </Link>
              </div>
            )}

            {/* Comments List */}
            {commentsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-purple animate-spin" />
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No comments yet. Be the first to share your thoughts!
              </div>
            ) : (
              <div className="space-y-6">
                {comments.map((comment) => (
                  <div key={comment.comment_id} className="flex gap-4" data-testid="comment-item">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                      <span className="text-slate-400 font-medium">
                        {comment.user_name?.charAt(0).toUpperCase() || "?"}
                      </span>
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{comment.user_name}</span>
                          <span className="text-xs text-slate-500">
                            {new Date(comment.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        {(user?.user_id === comment.user_id || user?.role === "admin" || user?.role === "super_admin") && (
                          <button
                            onClick={() => handleDeleteComment(comment.comment_id)}
                            className="text-slate-500 hover:text-red-400 transition-colors p-1"
                            title="Delete comment"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <p className="text-slate-300 whitespace-pre-wrap">{comment.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </article>
    </div>
  );
}
